import {ChangeDetectionStrategy, Component, Injector, ViewChild} from '@angular/core';
import {PlayerContainer} from "./player.container";
import {forkJoin, interval, mergeWith, Observable, of, Subject, takeUntil} from "rxjs";
import {catchError, delay, map, repeat, switchMap} from "rxjs/operators";
import {FlickingError} from "@egjs/ngx-flicking";
import {Log} from "../services/Log";

@Component({
  selector: 'app-player-list',
  template: '',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlayerList extends PlayerContainer {
  refreshEnabled = false;
  refreshTime = 3600 * 1000;
  itemInterval = 10 * 1000;
  randomOrder;

  options;
  pages = [];
  page;
  lastRefresh = 0;
  index;
  invalidatePanels = true;

  @ViewChild('flk', {static: true} ) flk;

  constructor(protected  injector: Injector) {
    super(injector);
  }

  getConfig() { return null; }
  getScene() { return null; }


  getPage(): Observable<any> {
    return of(this.getScene());
  }

  load(playerObject: any): Observable<any> {
    let self;
    return super.load(playerObject).pipe(
      switchMap( playerBase => {
        self = playerBase;
        this.scaleX = this.parentPlayer.scaleX;
        this.scaleY = this.parentPlayer.scaleY;
        this.options = { align: 'prev' };
        const config = this.getConfig();
        this.displayMode = (config.displayMode!=null) ? config.displayMode : 'slideShow';
        this.options.horizontal = (config.vertical=='0');
        this.randomOrder = (config.randomOrder=='1');
        if (config.itemInterval!=null && config.itemInterval!="") {
          this.itemInterval = Number(config.itemInterval) * 1000;
        }
        if (config.minRefreshTime!=null && config.minRefreshTime!="") {
          this.refreshEnabled = (config.refreshEnabled == '1');
          this.refreshTime = (config.minRefreshTime != null) ? (Number(config.minRefreshTime) * 60 * 1000) : (3600 * 1000);
        }
        this.playVideoInFull = (config.playVideoInFull == '1');


        if (config.volume!=null) {
          this.volume = Number(config.volume);
        }

        this.loadStat();

        const src = this.getScene().$.src;
        if (src=="" || src=="-1") {
          this.showMsg(this.playerObject.$.label, 'No scene assigned');
          return of(null);
        } else {
          this.showMsg(null, null);

          if (this.editMode) {
            return this.showPage();
          }
          return of('load');
        }
      }),
      catchError( err => this.logError(err)),
      map(a => self),
      takeUntil(this.finish$)
    )
  }

  play(): Observable<any> {
    if (this.editMode) {
      this.display(true);
      return of('play');
    }

    let trigger$:Observable<any> = super.play();
    if (this.refreshEnabled) {
      trigger$ = trigger$.pipe(
        mergeWith(interval(this.refreshTime))
      )
    }

    return trigger$.pipe(
      switchMap(r1 => this.loadProviderData()),
      catchError( err => of(null)),  // fail to load url
      switchMap( value => {
        if (value == null) {
          return of(null);
        }
        return this.shuffleList(value).pipe(
          switchMap(value => {
            this.providerData = value;
            if (this.displayMode == 'list') {
              return this.showList();
            } else if (this.displayMode == 'slideShow') {
              this.index = -1;
              return this.slideShow();
            } else {
              this.index = 0;
              return this.manual();
            }
          })
        )
      })
    )
  }

  shuffleList(list) {
    this.lastRefresh = new Date().getTime();
    if (this.randomOrder) {
      let shuffled = list
        .map(value => ({value, sort: Math.random()}))
        .sort((a, b) => a.sort - b.sort)
        .map(({value}) => value)
      return of(shuffled);
    } else {
      return of(list);
    }
  }


  showList(): Observable<any> {
    this.pages = [];
    let index = 0;
    if (this.providerData==null) {
      return of(null);
    }
    (<any>this.providerData).forEach( item => {
      const pg = { Player: [this.getScene()], childProviderData: this.providerData[index++]};
      this.pages.push(pg);
    })
    this.changeDetectorRef.detectChanges();
    const list = [];
    this.pages.forEach(page => {
      list.push(page.$obs);
    })
    return forkJoin(list);
  }

  slideShow(): Observable<any> {
    return of('loop').pipe(
      switchMap(a => {
        this.index = (this.index<this.providerData.length-1) ? this.index + 1 : 0;
        this.childProviderData = this.providerData[this.index];
        const interval$ = of('interval').pipe(delay(this.itemInterval));
        const showPage$ = this.showPage();
        return forkJoin(interval$, showPage$);
      }),
      repeat(),
    )
  }


  manual(): Observable<any> {
    this.childProviderData = this.providerData[this.index];
    return this.showPage();
  }


  loadProviderData(): Observable<any> {
    return of(null);
  }

  updatePanels() {
    if (this.invalidatePanels) {
      this.invalidatePanels = false;
      this.flk.moveTo(0).catch(err => {
        if (err instanceof FlickingError)
          return;
        throw err;
      });

    }
    this.flk.resize();
  }

  onCommand(command, param): Observable<any> {
    const cmd = command.$.command;

    const length = (this.providerData!=null) ? this.providerData.length : 0;

    switch (cmd) {
      case 'firstPage':
        if (length>0) {
          if (this.displayMode=='list') {
            this.flk.moveTo(0).catch(err => {
              if (err instanceof FlickingError)
                return;
              throw err;
            });
            return of(null);
          } else {
            this.index = 0;
            this.childProviderData = this.providerData[this.index];
            return this.showPage();
          }
        } else {
          return of(null);
        }
      case 'prevPage':
        if (length>0) {
          if (this.displayMode=='list') {
            this.flk.prev().catch(err => {
              if (err instanceof FlickingError)
                return;
              throw err;
            });
            return of(null);
          } else {
            this.index = (this.index > 0) ? this.index - 1 : length - 1;
            this.childProviderData = this.providerData[this.index];
            return this.showPage();
          }
        } else {
          return of(null);
        }
      case 'nextPage':
        if (length>0) {
          if (this.displayMode=='list') {
            this.flk.next().catch(err => {
              if (err instanceof FlickingError)
                return;
              throw err;
            });
            return of(null);
          } else {
            this.index = (this.index < length - 1) ? this.index + 1 : 0;
            this.childProviderData = this.providerData[this.index];
            return this.showPage();
          }
        } else {
          return of(null);
        }
      case 'lastPage':
        if (length>0) {
          if (this.displayMode=='list') {
            this.flk.moveTo(length-1).catch(err => {
              if (err instanceof FlickingError)
                return;
              throw err;
            });
            return of(null);
          } else {
            this.index = length - 1;
            this.childProviderData = this.providerData[this.index];
            return this.showPage();
          }
        } else {
          return of(null);
        }

      case 'filter':
        return of(null); //???

      case 'load':
        return this.loadProviderData();

      case 'save':
        return this.playerService.saveDataProvider( this.data.DataProvider[0].$.eventName, this.providerData, false);
    }
    return of(null);
  }
}
